import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { PROJECT_TYPES, SITE } from "../lib/site";
import SectionHeader from "../components/SectionHeader";
import Seo from "../components/Seo";

const PROJECT_SIZES = ["Small (under £10k)", "Medium (£10k–£50k)", "Large (£50k–£150k)", "Major (£150k+)"];
const TIMELINES = ["ASAP", "Within 1 month", "1–3 months", "3–6 months", "6+ months / planning stage"];

export default function Quote() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        project_size: "",
        budget: "",
        timeline: "",
        address: "",
        description: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.project_type || !form.description) {
            toast.error("Please fill in all required fields.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/quotes", form);
            setDone(true);
            toast.success("Quote request received. We'll be in touch within 1 working day.");
        } catch (err) {
            toast.error("Submission failed. Please call us on " + SITE.phone);
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="section-pad container-x" data-testid="quote-success">
                <div className="max-w-2xl mx-auto text-center card-earthy">
                    <CheckCircle2 size={56} className="mx-auto text-primary" strokeWidth={1.5} />
                    <h2 className="mt-6 text-3xl md:text-4xl font-heading font-extrabold">Thanks, {form.name.split(" ")[0]}!</h2>
                    <p className="mt-4 text-muted-foreground leading-relaxed">
                        We've received your quote request. One of our team will review the details and get
                        back to you within one working day to arrange a free site visit.
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Need it urgently? Give us a call on <a className="text-primary font-medium" href={SITE.phoneLink}>{SITE.phone}</a>.
                    </p>
                    <button
                        onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", project_type: "", project_size: "", budget: "", timeline: "", address: "", description: "" }); }}
                        className="btn-outline mt-8"
                        data-testid="quote-reset-btn"
                    >
                        Submit another
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="quote-page">
            <Seo
                title="Get a Free Quote — Fixed-Price Building Quotes"
                description="Tell us about your build. Free site visit, fixed-price written quote. Extensions, lofts, groundworks, electrical and mechanical across Berkshire."
                path="/quote"
            />
            <section className="bg-muted border-b border-border">
                <div className="container-x py-20 md:py-28">
                    <SectionHeader
                        eyebrow="Free Quote"
                        title="Get a no-pressure, fixed-price quote."
                        lead="Tell us about your project below. We'll arrange a free site visit and follow up with a detailed written quotation."
                    />
                </div>
            </section>

            <section className="section-pad container-x">
                <form onSubmit={submit} className="max-w-3xl mx-auto card-earthy space-y-6" data-testid="quote-form">
                    {/* Step 1 */}
                    <div>
                        <div className="eyebrow">Step 1</div>
                        <h3 className="mt-2 font-heading text-xl font-bold">Your details</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Full name *">
                            <input value={form.name} onChange={update("name")} className="input-style" data-testid="quote-name" required />
                        </Field>
                        <Field label="Phone *">
                            <input value={form.phone} onChange={update("phone")} className="input-style" data-testid="quote-phone" required />
                        </Field>
                        <Field label="Email *">
                            <input type="email" value={form.email} onChange={update("email")} className="input-style" data-testid="quote-email" required />
                        </Field>
                        <Field label="Project address (postcode)">
                            <input value={form.address} onChange={update("address")} className="input-style" data-testid="quote-address" />
                        </Field>
                    </div>

                    {/* Step 2 */}
                    <div className="pt-4">
                        <div className="eyebrow">Step 2</div>
                        <h3 className="mt-2 font-heading text-xl font-bold">Project details</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Project type *">
                            <select value={form.project_type} onChange={update("project_type")} className="input-style" data-testid="quote-project-type" required>
                                <option value="">Select…</option>
                                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Approximate size">
                            <select value={form.project_size} onChange={update("project_size")} className="input-style" data-testid="quote-project-size">
                                <option value="">Select…</option>
                                {PROJECT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="Budget range (optional)">
                            <input value={form.budget} onChange={update("budget")} className="input-style" placeholder="e.g. £30k – £45k" data-testid="quote-budget" />
                        </Field>
                        <Field label="Ideal timeline">
                            <select value={form.timeline} onChange={update("timeline")} className="input-style" data-testid="quote-timeline">
                                <option value="">Select…</option>
                                {TIMELINES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Tell us about the project *">
                        <textarea rows={6} value={form.description} onChange={update("description")} className="input-style resize-none" placeholder="What you're hoping to do, any drawings/planning, key requirements…" data-testid="quote-description" required />
                    </Field>

                    <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between pt-2">
                        <p className="text-xs text-muted-foreground max-w-md">
                            We'll use your details only to respond to this enquiry. No spam, no sharing. Promise.
                        </p>
                        <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="quote-submit-btn">
                            {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                            {loading ? "Sending…" : "Request My Quote"}
                        </button>
                    </div>

                    <style>{`.input-style{background:#fff;border:1px solid hsl(var(--border));border-radius:0.5rem;padding:0.75rem 1rem;width:100%;font-size:0.95rem;transition:all 0.15s;color:hsl(var(--foreground))}.input-style:focus{outline:none;border-color:hsl(var(--primary));box-shadow:0 0 0 3px hsla(155,36%,18%,0.12)}`}</style>
                </form>
            </section>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2">{label}</span>
            {children}
        </label>
    );
}
